import React, { useState, useEffect, useRef } from 'react'
import Portal from 'components/Portal/Portal'
import { Modal } from 'components/Modal/Modal'
import { Spinner } from '../Loaders/Loaders'
import CaretDownIcon from '@tecuity/sos-icons/react/caret-down'
import CaretUpIcon from '@tecuity/sos-icons/react/caret-up'
import DownloadIcon from '@tecuity/sos-icons/react/download-box'
import FileDownloader from '../ImageViewer/FileDownloader'
import { BASE_URL } from 'requests'
import * as api from '../../requests'
import '../RecordHistory/RecordHistory.scss'

const FilingCard = ({ filing, index, allExpanded, expandedIds, onToggle }) => {
  const downloaderRef = useRef(null)
  const isExpanded = allExpanded || !!expandedIds[index]
  const downloadUrl = filing.ShowDownloadButton && filing.ImageNum
    ? BASE_URL + '/api/Miscellaneous/PublicDetail/Image/' + filing.ImageNum
    : null

  const handleDownload = () => {
    if (downloaderRef.current) {
      downloaderRef.current.download()
    }
  }

  return (
    <div className="amendment-wrapper">
      <button
        className={'title' + (isExpanded ? '' : ' collapsed')}
        onClick={() => onToggle(index)}
        aria-expanded={isExpanded}
      >
        {filing.FilingType}
        {filing.ReceivedDate && (
          <span>{'- ' + new Date(filing.ReceivedDate).toLocaleDateString()}</span>
        )}
        {isExpanded ? <CaretUpIcon /> : <CaretDownIcon />}
      </button>
      {isExpanded && (
        <div className="amendment-drawer" aria-live="polite">
          <div className={'amendment' + (!filing.FieldChanges || filing.FieldChanges.length === 0 ? ' no-history' : '')}>
            <div className="detail">
              <label>Filing Type</label>
              <span>{filing.FilingType}</span>
            </div>
            <div className="detail">
              <label>{'Filing #'}</label>
              <span>{filing.FilingNum}</span>
            </div>
            {filing.ReceivedDate && (
              <div className="detail">
                <label>Date</label>
                <span>{new Date(filing.ReceivedDate).toLocaleDateString()}</span>
              </div>
            )}
            {downloadUrl && (
              <div className="detail">
                <label>Image Download</label>
                <span>
                  <button
                    className="btn btn-raised btn-primary"
                    onClick={handleDownload}
                  >
                    <DownloadIcon />
                    {' Download'}
                  </button>
                </span>
              </div>
            )}
          </div>
          {filing.FieldChanges && filing.FieldChanges.length > 0 && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Field Name</th>
                    <th>Changed From</th>
                    <th>Changed To</th>
                  </tr>
                </thead>
                <tbody>
                  {filing.FieldChanges.map((change, i) => (
                    <tr key={i}>
                      <td>{change.FieldLabel}</td>
                      <td>{change.ChangedFrom}</td>
                      <td>{change.ChangedTo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {downloadUrl && (
        <FileDownloader
          fileName={(filing.FilingNum || 'filing') + '.pdf'}
          href={downloadUrl}
          ref={downloaderRef}
        />
      )}
    </div>
  )
}

const MiscellaneousPublicHistoryModal = ({ show, onDismiss, recordNum }) => {
  const [filings, setFilings] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [allExpanded, setAllExpanded] = useState(false)
  const [expandedIds, setExpandedIds] = useState({})

  useEffect(() => {
    if (!show) {
      return
    }
    setFilings([])
    setLoading(true)
    setError(false)
    setAllExpanded(false)
    setExpandedIds({})
    api.GET_MISC_PUBLIC_FILING_HISTORY(
      recordNum,
      (data) => {
        setFilings(data || [])
        setLoading(false)
      },
      () => {
        setError(true)
        setLoading(false)
      }
    )
  }, [show])

  const handleToggleAll = () => {
    setAllExpanded((prev) => !prev)
    setExpandedIds({})
  }

  const handleToggleCard = (index) => {
    setExpandedIds((prev) => Object.assign({}, prev, { [index]: !prev[index] }))
  }

  return (
    <Portal isOpened={show}>
      <Modal
        title="Filing History"
        closeModal={onDismiss}
        closeButton
        modalSize="fullscreen"
        modalBodyClassName="history-modal"
      >
        <div className="record-history">
          <div className="toolbar">
            <div className="num-filings" role="status">
              {'Total filings: ' + filings.length}
            </div>
            <button
              className="btn btn-raised btn-primary"
              onClick={handleToggleAll}
              disabled={loading || filings.length === 0}
            >
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          </div>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
              <Spinner themeColor="secondary" size="medium" />
            </div>
          )}
          {!loading && error && (
            <p style={{ textAlign: 'center', padding: '24px 0' }}>Unable to load filing history.</p>
          )}
          {!loading && !error && filings.length === 0 && (
            <p style={{ textAlign: 'center', padding: '24px 0' }}>There is no filing history for this record.</p>
          )}
          {!loading && !error && filings.map((filing, index) => (
            <FilingCard
              key={index}
              filing={filing}
              index={index}
              allExpanded={allExpanded}
              expandedIds={expandedIds}
              onToggle={handleToggleCard}
            />
          ))}
        </div>
      </Modal>
    </Portal>
  )
}

export default MiscellaneousPublicHistoryModal